import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { uploadInChunks, downloadFile, formatBytes } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Paperclip, FileDown, Trash2, Loader2, Image as ImageIcon, FileText } from "lucide-react";
import { toast } from "sonner";

// Attachments (photos, PDFs, documents) stored in Emergent object storage; downloads go through role-checked backend.
// `purpose` = "incident" (evidence) or "matter" (supporting files for a Policy-Guided Review).
export default function IncidentAttachments({ incident, canEdit, onChanged, purpose = "incident", title = "Evidence attachments", emptyHint }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);
  const items = incident.attachments || [];
  const tid = purpose === "incident" ? "incident-attachments" : "matter-attachments";

  const pick = () => fileRef.current?.click();
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("File exceeds the 25 MB limit");
    setBusy(true); setProgress(0);
    try {
      const rec = await uploadInChunks(file, { purpose, ref_id: incident.id, caption }, setProgress);
      onChanged({ ...incident, attachments: [...items, rec] });
      setCaption("");
      toast.success(`Attached ${rec.original_filename}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); setProgress(0); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove "${a.original_filename}" from this report?`)) return;
    try { await api.delete(`/files/${a.id}`); onChanged({ ...incident, attachments: items.filter((x) => x.id !== a.id) }); toast.success("Attachment removed"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div data-testid={tid} className="p-4 rounded-xl border border-slate-200 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-display font-bold text-[#0A2A5E] text-sm flex items-center gap-2"><Paperclip className="w-4 h-4" /> {title} <span className="text-xs font-normal text-slate-500">({items.length})</span></div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Input data-testid="attachment-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="h-8 text-xs w-44" disabled={busy} />
            <input ref={fileRef} data-testid="attachment-file-input" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.txt,.csv,.docx,.xlsx,.mp3,.mp4,.mov,.m4a" onChange={upload} />
            <Button data-testid="attachment-add-btn" size="sm" variant="outline" onClick={pick} disabled={busy} className="h-8 border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]">
              {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {progress}%</> : <><Paperclip className="w-3.5 h-3.5 mr-1.5" /> Add file</>}
            </Button>
          </div>
        )}
      </div>
      {busy && <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div data-testid="attachment-progress" className="h-full bg-[#F5A623] transition-[width]" style={{ width: `${progress}%` }} /></div>}
      {items.length === 0 ? (
        <p className="text-xs text-slate-500">{emptyHint || "No files attached. Attach photos, PDFs or documents that were preserved as evidence (max 25 MB each). Do not attach unnecessary medical or protected personal information."}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((a) => (
            <li key={a.id} data-testid={`attachment-${a.id}`} className="py-2 flex items-center gap-3 text-xs">
              {a.content_type?.startsWith("image/") ? <ImageIcon className="w-4 h-4 text-[#7C3F00] flex-shrink-0" /> : <FileText className="w-4 h-4 text-[#0A2A5E] flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{a.original_filename}</div>
                <div className="text-slate-500">{formatBytes(a.size)} · {a.uploaded_by_name} · {a.created_at?.slice(0, 16).replace("T", " ")}{a.caption ? ` · ${a.caption}` : ""}</div>
              </div>
              <button type="button" data-testid={`attachment-download-${a.id}`} onClick={() => downloadFile(a.id, a.original_filename)} className="p-1.5 rounded hover:bg-slate-100 text-[#0A2A5E]" aria-label="Download"><FileDown className="w-4 h-4" /></button>
              {canEdit && <button type="button" data-testid={`attachment-delete-${a.id}`} onClick={() => remove(a)} className="p-1.5 rounded hover:bg-rose-50 text-rose-700" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-slate-500">Files are stored in encrypted object storage and served only through LUMIS HR with role checks. Access is recorded in the audit trail.</p>
    </div>
  );
}
